// S-04 Protection's search params (Frontend §7.1 "Protection: decision, range, from, to, page"): the security events
// filters and page, in the URL. Date presets resolve in the clinic's zone (API.md §1.4).
import type { ListSecurityEventsParams, SecurityDecision } from '@pa/api/admin'
import { addDays, presetRange, rangeInstants, type DatePreset, type DateRangeValue, type LocalDate } from '@pa/ui'
import { DECISION_LABELS } from './labels'

/** No date filter = the API's default, the last 7 days (API.md §7.16). */
export const EVENT_PRESETS: readonly DatePreset[] = [
  { id: 'any', label: 'Last 7 days' },
  { id: 'today', label: 'Today' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'month', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
]

export interface ProtectionSearch {
  decision?: SecurityDecision
  range?: string
  from?: LocalDate
  to?: LocalDate
  page?: number
}

const DATE = /^\d{4}-\d{2}-\d{2}$/
const str = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined)
const isDecision = (v: unknown): v is SecurityDecision => typeof v === 'string' && v in DECISION_LABELS

/** The URL → valid Protection filters (unknown values are dropped). */
export function parseProtectionSearch(raw: Record<string, unknown>): ProtectionSearch {
  const out: ProtectionSearch = {}
  if (isDecision(raw.decision)) out.decision = raw.decision
  const range = str(raw.range)
  const from = str(raw.from)
  const to = str(raw.to)
  if (range === 'custom') {
    if (from && to && DATE.test(from) && DATE.test(to) && from <= to) Object.assign(out, { range, from, to })
  } else if (range && range !== 'any' && EVENT_PRESETS.some((p) => p.id === range)) {
    out.range = range
  }
  const page = typeof raw.page === 'number' ? raw.page : Number(str(raw.page))
  if (Number.isInteger(page) && page > 1) out.page = page
  return out
}

/** #56's query: presets at local midnight in the clinic's zone (`to` exclusive), a custom range inclusive. */
export function toEventParams(s: ProtectionSearch, timeZone: string, today: LocalDate): ListSecurityEventsParams {
  const params: ListSecurityEventsParams = {}
  if (s.decision) params.decision = s.decision
  const range =
    s.range === 'custom' && s.from && s.to
      ? { from: s.from, to: addDays(s.to, 1) }
      : s.range
        ? presetRange(s.range, today)
        : null
  if (range) Object.assign(params, rangeInstants(range, timeZone))
  if (s.page) params.page = String(s.page)
  return params
}

export const eventRangeValue = (s: ProtectionSearch): DateRangeValue =>
  s.range === 'custom'
    ? { id: 'custom', ...(s.from && { from: s.from }), ...(s.to && { to: s.to }) }
    : { id: s.range ?? 'any' }

export const hasEventFilters = (s: ProtectionSearch) => s.decision !== undefined || s.range !== undefined
